import React, { useEffect, useState } from "react";
import { InputNumber } from "antd";
import { useLocalStorage } from "../../../hooks";
import { convertPerHaToPerAcre, getDensityUnitLabel } from "../../../utils/unitConversions.js";

const StandCountCard = ({ areaUnit, siteMetadata, currentCrop, currentGreenhouse, currentPlot }) => {
  const [totalActualHeadCount, setTotalActualHeadCount] = useState(null);
  const [headCountPerHectare, setHeadCountPerHectare] = useState(null);
  const [harvestedHeadCount, setHarvestedHeadCount] = useState(null);

  const [recoveryRate, setRecoveryRate] = useLocalStorage(
    "broccoliStandCountLastRecoveryRate",
    null,
  );

  useEffect(() => {
    if (siteMetadata && currentCrop && currentGreenhouse && currentPlot) {
      const rawHeadCount =
        siteMetadata?.[currentGreenhouse.name]?.[currentPlot.name]?.actual_headcount;
      const rawArea =
        siteMetadata?.[currentGreenhouse.name]?.[currentPlot.name]?.table_data?.[0]?.area;
      const rawHarvestedHeadCount =
        siteMetadata?.[currentGreenhouse.name]?.[currentPlot.name]?.["harvested_headcount"];
      const headCount = Math.round(Number(rawHeadCount));
      const area = Number(rawArea);
      const harvestedHeadCount = Math.round(Number(rawHarvestedHeadCount));

      if (!Number.isNaN(harvestedHeadCount) && harvestedHeadCount > 0) {
        const standCount = harvestedHeadCount;
        setHarvestedHeadCount(standCount);
      } else {
        setHarvestedHeadCount(null);
      }

      if (!Number.isNaN(headCount) && !Number.isNaN(area) && headCount > 0 && area > 0) {
        const headCountPerHectare = Math.round(headCount / area);
        setHeadCountPerHectare(headCountPerHectare);
      } else {
        setHeadCountPerHectare(null);
      }

      if (!Number.isNaN(headCount)) {
        setTotalActualHeadCount(headCount);
      } else {
        setTotalActualHeadCount(null);
      }
    }
  }, [siteMetadata, currentCrop, currentGreenhouse, currentPlot]);

  let crateCount = null;
  if (
    totalActualHeadCount != null &&
    !Number.isNaN(totalActualHeadCount) &&
    typeof recoveryRate === "number"
  ) {
    crateCount = parseInt(Math.ceil((totalActualHeadCount * (recoveryRate / 100)) / 25));
  }

  const handleRecoveryRateChange = (val) => {
    if (typeof val === "number") {
      setRecoveryRate(val);
    } else {
      setRecoveryRate(null);
    }
  };

  return (
    <>
      <div className="stand-count-card flex lg:flex-col flex-row w-full mb-4">
        <div className="headsize-section w-full flex flex-col items-center lg:justify-center justify-start px-10 sm:px-4 py-4 lg:mr-0 mr-4 lg:mb-4 rounded-md shadow-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101">
          <h1 className="font-bold text-lg mb-4 text-center">Total Stand Count</h1>
          <h2 className="text-4xl font-semibold text-primaryBlue mb-2 text-center">
            {totalActualHeadCount ?? "N/A"}
          </h2>
          <p className="text-sm  text-center">
            Total actual head count for {currentGreenhouse?.name ?? "Greenhouse"} in{" "}
            {currentPlot?.name ?? "Plot"}
          </p>
        </div>

        {harvestedHeadCount != null && (
          <div className="headsize-section w-full flex flex-col items-center lg:justify-center justify-start px-10 sm:px-4 py-4 lg:mr-0 mr-4 lg:mb-4 rounded-md shadow-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101">
            <h1 className="font-bold text-lg mb-4 text-center">Harvested Stand Count</h1>
            <h2 className="text-4xl font-semibold text-primaryBlue mb-2 text-center">
              {harvestedHeadCount}
            </h2>
            <p className="text-sm  text-center">
              Head count for {currentGreenhouse?.name ?? "Greenhouse"} in{" "}
              {currentPlot?.name ?? "Plot"}
            </p>
          </div>
        )}

        <div className="headsize-section w-full flex flex-col items-center lg:justify-center justify-start px-10 sm:px-4 py-4 lg:mr-0 mr-4 lg:mb-4 rounded-md shadow-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101">
          <h1 className="font-bold text-lg mb-4 text-center">Total Stand Count {getDensityUnitLabel(areaUnit)}</h1>
          <h2 className="text-4xl font-semibold text-primaryBlue mb-2 text-center">
            {headCountPerHectare != null && areaUnit === "acre"
              ? Math.round(convertPerHaToPerAcre(headCountPerHectare))
              : headCountPerHectare ?? "N/A"}
          </h2>
          <p className="text-sm  text-center">
            {currentGreenhouse?.name ?? "Greenhouse"} in {currentPlot?.name ?? "Plot"}
          </p>
        </div>

        {/* Yield Estimation Card */}
        <div className="headsize-section w-full flex flex-col items-center justify-center px-10 py-4 sm:px-4 rounded-md shadow-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101">
          <h1 className="font-bold text-lg mb-4 text-center">Yield Estimation</h1>

          <div className="mb-4">
            <span className="mb-2 text-sm text-center">Enter recovery rate (%): &nbsp;</span>
            <InputNumber
              min={0}
              max={100}
              step={1}
              precision={0}
              disabled={totalActualHeadCount == null || Number.isNaN(totalActualHeadCount)}
              value={recoveryRate}
              onChange={handleRecoveryRateChange}
            />
            <span className="ml-2">%</span>
          </div>

          {totalActualHeadCount != null &&
            !Number.isNaN(totalActualHeadCount) &&
            crateCount != null && (
              <>
                <h2 className="text-lg font-semibold text-primaryBlue text-center">
                  Expected no. of crates = {crateCount} crates
                </h2>
              </>
            )}
        </div>
      </div>
    </>
  );
};

export { StandCountCard };
